/**
 * AppErrorBoundary - Defesa em profundidade contra erros fatais do React.
 *
 * Razão de existir:
 *  - Quando muitos componentes são desmontados/remontados sob storm de
 *    chamadas (ex.: subscription expirou e dezenas de requests falham com 402),
 *    o React pode lançar o erro #185 (Maximum update depth exceeded) e abortar
 *    a árvore inteira — deixando a tela em branco, inclusive a página de
 *    `/expired-subscription` que deveria ter sido renderizada.
 *  - Este boundary captura o erro, recupera o estado mínimo (token/dueDate) e
 *    exibe um fallback útil + tenta restaurar a navegação automaticamente para
 *    `/expired-subscription` ou `/login`.
 *
 * Não substitui um sistema de monitoramento (Sentry, etc.) — é apenas uma rede
 * de segurança para o usuário não ver tela branca.
 */

import React from "react";
import { reportClientError } from "../../helpers/clientErrorReporter";

// Só os invariantes de loop de update do React 17 (#185 nested updates, #301
// too many re-renders) — o storm de 402 quando a assinatura expira cai aqui.
// Ancorado no formato real de dev e de prod para "#185" dentro de uma mensagem
// de negócio (ex.: "pedido #1850") não redirecionar.
const UPDATE_LOOP_RE =
  /^Maximum update depth exceeded|^Too many re-renders|Minified React error #(185|301)(?!\d)/;

export function shouldRedirectToExpiredSubscription(error, { token, dueDate, isExpiredPath }) {
  if (!token || !dueDate || isExpiredPath) return false;
  return UPDATE_LOOP_RE.test(String(error?.message || ""));
}

// Chunk lazy que sumiu do servidor após um deploy (rsync troca os hashes).
// Um reload traz o index.html novo; a flag em sessionStorage evita loop.
const CHUNK_LOAD_RE = /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/i;
const CHUNK_RELOAD_FLAG = "appErrorBoundary:chunkReloaded";

export function isChunkLoadError(error) {
  return (
    error?.name === "ChunkLoadError" || CHUNK_LOAD_RE.test(String(error?.message || ""))
  );
}

export function shouldReloadForChunkError(error, { alreadyReloaded }) {
  return isChunkLoadError(error) && !alreadyReloaded;
}

const isExpiredPath = () => {
  try {
    return window.location.pathname === "/expired-subscription";
  } catch {
    return false;
  }
};

const safeReplace = (path) => {
  try {
    if (window.location.pathname !== path) {
      window.location.replace(path);
    }
  } catch {
    /* ignore */
  }
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Se a app sobreviveu 60s após o reload, o chunk novo carregou: libera um
    // próximo auto-reload para o deploy seguinte. Antes disso, um novo
    // ChunkLoadError cai no fallback (evita loop de reload).
    this.chunkFlagTimer = setTimeout(() => {
      try {
        window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      } catch {
        /* ignore */
      }
    }, 60000);
  }

  componentWillUnmount() {
    clearTimeout(this.chunkFlagTimer);
  }

  componentDidCatch(error, info) {
    try {
      // Log estruturado: ajuda a debugar em produção (Sentry/console).
      // eslint-disable-next-line no-console
      console.error("[AppErrorBoundary] Erro fatal capturado:", {
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack
      });
    } catch {
      /* ignore */
    }

    try {
      reportClientError({ error, source: "app-boundary", componentStack: info?.componentStack });
    } catch {
      /* telemetria é best-effort */
    }

    try {
      if (
        shouldReloadForChunkError(error, {
          alreadyReloaded: window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "1",
        })
      ) {
        window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
        window.location.reload();
        return;
      }
    } catch {
      /* ignore */
    }

    // Só redireciona no storm de subscription (#185). Qualquer TypeError de
    // render (ex.: i18n.t unbound) também tem token+dueDate no localStorage —
    // mandar pra /expired-subscription parece "refresh total" do chat.
    try {
      if (
        shouldRedirectToExpiredSubscription(error, {
          token: window.localStorage.getItem("token"),
          dueDate: window.localStorage.getItem("companyDueDate"),
          isExpiredPath: isExpiredPath(),
        })
      ) {
        safeReplace("/expired-subscription");
      }
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  handleGoLogin = () => {
    try {
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("companyDueDate");
    } catch {
      /* ignore */
    }
    safeReplace("/login");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isDark = (() => {
      try {
        return document.documentElement.getAttribute("data-theme") === "dark";
      } catch {
        return false;
      }
    })();

    const palette = isDark
      ? {
          bg: "#0b141a",
          paper: "#111b21",
          border: "rgba(255,255,255,0.08)",
          text: "#e9edef",
          textSecondary: "#8696a0",
          accent: "#00a884"
        }
      : {
          bg: "#eae6df",
          paper: "#ffffff",
          border: "rgba(0,0,0,0.08)",
          text: "#111b21",
          textSecondary: "#667781",
          accent: "#008069"
        };

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: palette.bg,
          padding: 24,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: palette.text
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: palette.paper,
            border: `1px solid ${palette.border}`,
            borderRadius: 16,
            padding: 32,
            boxShadow: isDark
              ? "0 2px 8px rgba(0,0,0,0.25)"
              : "0 2px 8px rgba(0,0,0,0.06)",
            textAlign: "center"
          }}
        >
          <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 600 }}>
            Algo inesperado aconteceu
          </h1>
          <p
            style={{
              margin: "0 0 20px",
              color: palette.textSecondary,
              fontSize: 14,
              lineHeight: 1.5
            }}
          >
            Recarregue a página para continuar. Se o problema persistir, faça
            login novamente.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap"
            }}
          >
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "10px 20px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Recarregar
            </button>
            <button
              type="button"
              onClick={this.handleGoLogin}
              style={{
                background: "transparent",
                color: palette.text,
                border: `1px solid ${palette.border}`,
                padding: "10px 20px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer"
              }}
            >
              Ir para login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
